import { isDefined } from '@sgme/fp';
import classnames from 'classnames';
import { FC, ReactNode } from 'react';
import { Nav, TabContainer, TabContent, TabPane } from 'react-bootstrap';
import styled from 'styled-components';

import type { ChildrenTabArray } from './Tab';
import { TabHeader } from './TabHeader';

interface TabsProps {
  children: ReactNode;
  id: string;
  className?: string;
  activeTabId: string | undefined;
  onTabActivated: (tab: string) => void;
  navFill?: boolean;
  withShadow?: boolean;
  navClassName?: string;
  tabPaneClassName?: string;
  boxShadowBorder?: boolean;
}

type StylingProps = { hasshadowborder: string };

const StyledNav = styled(Nav)<StylingProps>`
  ${props =>
    props.hasshadowborder === 'true' &&
    `
      box-shadow: inset 0 0 0 1px var(--bs-border-color);
      padding-bottom: 1px;
    `}
`;

export const Tabs: FC<TabsProps> = ({
  id,
  className,
  activeTabId,
  children,
  onTabActivated,
  navFill = false,
  withShadow = false,
  boxShadowBorder = false,
  navClassName,
  tabPaneClassName,
}) => {
  if (!isDefined(children)) {
    return null;
  }

  const tabsChildren: ChildrenTabArray = (children as ChildrenTabArray).filter(isDefined);

  const tabs = tabsChildren.map(child => ({ tabId: child.props?.tabId, label: child.props?.label }));
  const activeTab = activeTabId ?? (tabs.length > 0 ? tabs[0].tabId : undefined);

  return (
    <TabContainer activeKey={activeTab}>
      <div id={id} className={classnames('d-flex flex-column ', { 'overflow-hidden': withShadow }, className)}>
        <StyledNav
          hasshadowborder={boxShadowBorder.toString()}
          pills
          fill={navFill}
          as="ul"
          className={classnames('nav', { shadow: withShadow }, navClassName)}
        >
          {tabsChildren.map((child, index) => {
            return (
              <TabHeader
                className={child.props.className}
                beforeLabel={child.props.beforeLabel}
                afterLabel={child.props.afterLabel}
                key={child.props.tabId || index}
                tabId={child.props.tabId}
                label={child.props.label}
                activeTab={activeTab}
                disabled={child.props.disabled}
                onTabSelected={onTabActivated}
                e2eId={child.props.e2eId}
              />
            );
          })}
        </StyledNav>

        <TabContent className="flex-grow-1">
          {tabsChildren.map((child, index) => {
            const tabId = child.props.tabId;
            return (
              <TabPane className={tabPaneClassName} eventKey={tabId} key={tabId || index}>
                {child.props.children}
              </TabPane>
            );
          })}
        </TabContent>
      </div>
    </TabContainer>
  );
};

Tabs.displayName = 'FormTabs';
